/**
 * Разбор записи голосового: полоса волны и точная длительность — как в
 * WhatsApp/Telegram.
 *
 * И ВОЛНУ, И ДЛИТЕЛЬНОСТЬ берём из ОДНОГО декодирования. Сервер их из звука не
 * достаёт: это потребовало бы аудиокодеков в образе. А тот, кто записал,
 * декодирует свой же blob прямо в браузере. Длительность у декодированного
 * буфера точная (`buffer.duration`), в отличие от секундного таймера, который
 * у записи короче секунды даёт ноль. Не вышло (формат не декодировался, нет
 * WebAudio) — вернём null, и плеер/таймер обойдутся без них.
 *
 * BARS пиков хватает: WhatsApp рисует около полусотни, больше в узкий пузырь
 * не влезает. Значения нормируются в 0..1.
 */
const BARS = 48;

/** @returns {Promise<{peaks: number[]|null, duration: number|null}>} */
export async function analyzeVoice(blob) {
  const Ctx = window.AudioContext || window.webkitAudioContext;

  // Очень длинную запись не декодируем: полный PCM в памяти (≈115 МБ на 10
  // минут) и синхронный проход заморозили бы вкладку. Голосовое короткое.
  if (!Ctx || !blob || blob.size > 8 * 1024 * 1024) return { peaks: null, duration: null };

  let ctx;
  try {
    ctx = new Ctx();
    const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
    const data = buffer.getChannelData(0);
    const block = Math.floor(data.length / BARS) || 1;

    const peaks = [];
    for (let i = 0; i < BARS; i += 1) {
      // RMS по окну, а не одиночный отсчёт: одиночный ловит случайный щелчок и
      // рисует шип там, где было тихо.
      let sum = 0;
      const start = i * block;
      for (let j = 0; j < block; j += 1) {
        const v = data[start + j] || 0;
        sum += v * v;
      }
      peaks.push(Math.sqrt(sum / block));
    }

    // Нормируем к самому громкому: тихая запись иначе — почти плоская линия.
    const max = Math.max(...peaks, 0.0001);
    return {
      peaks: peaks.map((p) => Math.round((p / max) * 100) / 100),
      duration: Number.isFinite(buffer.duration) ? buffer.duration : null,
    };
  } catch {
    return { peaks: null, duration: null };
  } finally {
    // Контекст закрываем: каждый оставленный открытым съедает аудиоканал, и
    // через десяток записей браузер отказывает в новом.
    try { await ctx?.close(); } catch { /* уже закрыт */ }
  }
}
